import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { translationPropType } from '@eb/i18n';
import classnames from 'classnames';
import Button from 'eventbrite_design_system/button/Button';
import { HEADING_PROP_TYPES } from '../../constants/componentPropTypes';

import './bannerContent.scss';

const BANNER_CTA_BUTTON_CLASSES = 'eds-g-cell eds-g-cell--has-overflow';

const MOBILE_BANNER_CONTENT_CTA_CLASSES =
    'eds-g-cell eds-g-cell-1-1 eds-text--center eds-l-mar-vert-4';
const MOBILE_BANNER_CONTENT_TEXT_CLASSES =
    'eds-g-cell eds-g-cell-1-1 eds-text--center';

const DESKTOP_BANNER_CONTENT_CONTAINER_CLASSES = 'eds-align--center';
const DESKTOP_BANNER_CONTENT_CTA_CLASSES =
    'eds-g-cell eds-g-cell-4-12 eds-text--right';
const DESKTOP_BANNER_CONTENT_TEXT_CLASSES =
    'eds-g-cell eds-g-cell-8-12 eds-text--left eds-align--center-vertical';

const ContentText = ({
    description,
    heading,
    secondaryHeading,
    customHeadingSize,
    contentTextColor,
}) => {
    const headingClassNames = classnames(
        customHeadingSize ? customHeadingSize : 'eds-text-hs',
        `eds-text-color--${contentTextColor}`,
    );
    const bannerContentSecondaryHeadingClassNames = headingClassNames;

    return (
        <div>
            <h2 className={headingClassNames}>{heading}</h2>
            <h3 className={bannerContentSecondaryHeadingClassNames}>
                {secondaryHeading}
            </h3>
            <p className="marketing-default-text">{description}</p>
        </div>
    );
};

const CTAButton = ({ CTA, CTAStyle, additionalClassNames = '', onClick }) => {
    const buttonContainerClassName = `${BANNER_CTA_BUTTON_CLASSES} ${additionalClassNames}`;

    return (
        <div className={buttonContainerClassName}>
            <Button style={CTAStyle} onClick={onClick} disabled={!CTAStyle}>
                {CTA}
            </Button>
        </div>
    );
};

const MobileBannerContent = ({ contentText, contentCTA }) => (
    <div>
        <div className={MOBILE_BANNER_CONTENT_TEXT_CLASSES}>{contentText}</div>
        <div className={MOBILE_BANNER_CONTENT_CTA_CLASSES}>{contentCTA}</div>
    </div>
);

const DesktopBannerContent = ({ contentText, contentCTA }) => (
    <div className={DESKTOP_BANNER_CONTENT_CONTAINER_CLASSES}>
        <div className={DESKTOP_BANNER_CONTENT_TEXT_CLASSES}>{contentText}</div>
        <div className={DESKTOP_BANNER_CONTENT_CTA_CLASSES}>{contentCTA}</div>
    </div>
);

const BannerContentAlignRight = ({ contentText, contentCTA }) => (
    <div>
        <div className="eds-show-down-sm">
            <MobileBannerContent
                contentText={contentText}
                contentCTA={contentCTA}
            />
        </div>
        <div className="eds-show-up-sw">
            <DesktopBannerContent
                contentText={contentText}
                contentCTA={contentCTA}
            />
        </div>
    </div>
);

export default class BannerContent extends PureComponent {
    static propTypes = {
        ...HEADING_PROP_TYPES,
        /*
        CTA for button
        */
        CTA: translationPropType,
        /*
        CTA style for button
        */
        CTAStyle: PropTypes.string,
        /*
        Callback to trigger on click
        */
        onClick: PropTypes.func,
        /*
        Show CTA button to the right of content text
        */
        alignCTAButtonRight: PropTypes.bool,
        /*
        Align content text to the left
        */
        alignTextLeftWhenIllustrationIsShown: PropTypes.bool,
        /*
        Color for the content text
         */
        contentTextColor: PropTypes.string,
        /*
        Additional class names (space-delimited list) for the CTA element
         */
        additionalCTAClassNames: PropTypes.string,
    };

    static defaultProps = {
        alignTextLeftWhenIllustrationIsShown: false,
        contentTextColor: 'grey-900',
    };

    render() {
        const {
            alignTextLeftWhenIllustrationIsShown,
            alignCTAButtonRight,
            CTA,
            CTAStyle,
            customHeadingSize,
            description,
            heading,
            onClick,
            secondaryHeading,
            contentTextColor,
            additionalCTAClassNames,
        } = this.props;

        const bannerContentClasses = classnames('eds-g-cell eds-g-cell-1-1', {
            'marketing-banner-content--conditional-left eds-text--center': alignTextLeftWhenIllustrationIsShown,
        });

        const contentText = (
            <ContentText
                customHeadingSize={customHeadingSize}
                description={description}
                heading={heading}
                secondaryHeading={secondaryHeading}
                contentTextColor={contentTextColor}
            />
        );
        const contentCTA = (
            <CTAButton
                CTA={CTA}
                CTAStyle={CTAStyle}
                additionalClassNames={additionalCTAClassNames}
                onClick={onClick}
            />
        );
        let content = (
            <div className={bannerContentClasses}>
                {contentText}
                {contentCTA}
            </div>
        );

        if (alignCTAButtonRight) {
            content = (
                <BannerContentAlignRight
                    contentText={contentText}
                    contentCTA={contentCTA}
                />
            );
        }

        return content;
    }
}
